/* Explicit Turnstile widgets for the public writes that stay on the page.

   A native form that navigates away can take the token from a hidden field.
   The enquiry form and the chat both stay where they are, so each action has
   its own widget, minted with `execution: 'execute'`. The token is taken at
   the moment of the request, and the widget is reset once that request has
   settled, because a token is redeemed once.

   The script tag itself is added by the public layout. Admin screens never
   mount a widget and never load the script. */

function rejection(reason) {
  const error = new Error('turnstile');

  error.reason = reason;

  return error;
}

const widgets = {};

/* The site locales, and the Turnstile language each one asks the checkbox to
   speak. `auto` would follow the browser, which is not the language the
   visitor picked on the page. */
const widgetLanguages = {
  'en-MY': 'en',
  'zh-MY': 'zh-cn',
  'ms-MY': 'ms-my'
};

let widgetLanguage = 'en';

export function setWidgetLanguage(locale) {
  const next = widgetLanguages[locale] || 'en';
  const previous = widgetLanguage;

  widgetLanguage = next;

  if (previous === next || !window.turnstile) {
    return;
  }

  Object.keys(widgets).forEach((action) => {
    const widget = widgets[action];

    if (!widget) {
      return;
    }

    fail(widget);

    try {
      window.turnstile.remove(widget.id);
    } catch (error) {
      /* The node may already be gone. The new render replaces it. */
    }

    delete widgets[action];
    mount(widget.el, action, widget.sitekey);
  });
}

/* The chat box can be on screen twice: the page band and the open panel. The
   last one to claim it is the box the visitor is using, so that is where the
   send check is drawn. Closing it hands the check back to the box still open. */
const chatHosts = [];

function whenReady() {
  if (window.turnstile) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    const started = Date.now();
    const timer = setInterval(() => {
      if (window.turnstile) {
        clearInterval(timer);
        resolve();
      } else if (Date.now() - started > 10000) {
        clearInterval(timer);
        reject(rejection('failed'));
      }
    }, 50);
  });
}

/* Renders one widget into `el` for `action`. A later call with a new element,
   which is what an Inertia visit does when the layout remounts, drops the
   previous widget so execute is aimed at a node that is still in the document. */
export async function mount(el, action, sitekey) {
  if (!el || !sitekey) {
    return;
  }

  await whenReady();

  const current = widgets[action];

  if (current && current.el === el) {
    return;
  }

  if (current) {
    /* A check that was still open belonged to the box that just left. Ending it
       here is what stops a send from sitting on a bubble after the page changes. */
    fail(current);
    try {
      window.turnstile.remove(current.id);
    } catch (error) {
      /* The previous node is already gone. The new render is the one that matters. */
    }
  }

  const slot = { resolve: null, reject: null, interactive: null };

  const id = window.turnstile.render(el, {
    sitekey,
    action,
    execution: 'execute',
    /* Nothing is drawn until a challenge is actually required. `flexible`
       takes the width of the field. The host has to keep a real box: a zero
       height or a frame faded in from invisible leaves this as a blank page. */
    appearance: 'interaction-only',
    size: 'flexible',
    language: widgetLanguage,
    callback: (token) => {
      if (!slot.resolve) {
        return;
      }

      const resolve = slot.resolve;

      slot.resolve = null;
      slot.reject = null;
      resolve(token);
    },
    'error-callback': () => {
      if (!slot.reject) {
        return;
      }

      const reject = slot.reject;

      slot.resolve = null;
      slot.reject = null;
      slot.interactive = null;
      reject(rejection('failed'));
    },
    /* Fires once the checkbox is about to be shown, which is the moment the
       button can stop spinning and hand the click to the widget. */
    'before-interactive-callback': () => {
      if (!slot.interactive) {
        return;
      }

      const interactive = slot.interactive;

      slot.interactive = null;
      interactive();
    },
  });

  widgets[action] = { id, el, slot, sitekey };
}

/* A token for this action, minted now. Two minutes is how long a visitor has
   to solve a challenge before the wait is given up. The server still rejects
   a token that arrives late. */
export function token(action, onInteractive) {
  const widget = widgets[action];

  if (!widget || !window.turnstile) {
    return Promise.reject(rejection('failed'));
  }

  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      widget.slot.resolve = null;
      widget.slot.reject = null;
      widget.slot.interactive = null;
      reject(rejection('failed'));
    }, 120000);

    widget.slot.resolve = (value) => {
      clearTimeout(timer);
      widget.slot.interactive = null;
      resolve(value);
    };

    widget.slot.reject = (error) => {
      clearTimeout(timer);
      widget.slot.interactive = null;
      reject(error);
    };

    widget.slot.interactive = typeof onInteractive === 'function' ? onInteractive : null;

    window.turnstile.execute(widget.id);
  });
}

/* Settles a check that will not get a token: the box was removed, or the
   visitor left the page while it was still open. */
function fail(widget) {
  if (!widget || !widget.slot || !widget.slot.reject) {
    return;
  }

  const reject = widget.slot.reject;

  widget.slot.resolve = null;
  widget.slot.reject = null;
  widget.slot.interactive = null;
  reject(rejection('abandoned'));
}

export async function claimChat(messageEl, sitekey) {
  if (!messageEl || !sitekey) {
    return;
  }

  chatHosts.push({ messageEl, sitekey });
  await renderChat();
}

export async function releaseChat(messageEl) {
  const index = chatHosts.findIndex((host) => host.messageEl === messageEl);

  if (index !== -1) {
    chatHosts.splice(index, 1);
  }

  await renderChat();
}

async function renderChat() {
  const host = chatHosts[chatHosts.length - 1];

  if (!host) {
    drop('chat_message');

    return;
  }

  await mount(host.messageEl, 'chat_message', host.sitekey);
}

function drop(action) {
  const widget = widgets[action];

  if (!widget) {
    return;
  }

  fail(widget);

  if (window.turnstile) {
    try {
      window.turnstile.remove(widget.id);
    } catch (error) {
      /* The node is already gone with the box that held it. */
    }
  }

  delete widgets[action];
}

export function reset(action) {
  const widget = widgets[action];

  if (!widget || !window.turnstile) {
    return;
  }

  widget.slot.resolve = null;
  widget.slot.reject = null;
  widget.slot.interactive = null;

  try {
    window.turnstile.reset(widget.id);
  } catch (error) {
    /* A widget removed by navigation has nothing to reset. */
  }
}
